import { useState, useContext } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '../../../components/Auth/AuthProvider';
import { Box, TextField, Button, Typography } from '@mui/material';
import {
  createFeatureFlag,
  updateFeatureFlag,
  FeatureFlag,
} from '../services/featureFlagApi';

interface FeatureFlagFormProps {
  flag?: FeatureFlag;
  onCancel: () => void;
  onError: (error: string) => void;
}

export function FeatureFlagForm({
  flag,
  onCancel,
  onError,
}: Readonly<FeatureFlagFormProps>) {
  const { actions } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const [name, setName] = useState(flag?.name ?? '');
  const [description, setDescription] = useState(flag?.description ?? '');

  const isEditing = !!flag;

  const createMutation = useMutation({
    mutationFn: async () => {
      const token = await actions?.getAccessToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      return createFeatureFlag({ name, description }, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['featureFlags'] });
      onCancel();
    },
    onError: (error) => onError(error.message),
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      const token = await actions?.getAccessToken();
      if (!token || !flag) {
        throw new Error('Not authenticated or missing flag');
      }
      return updateFeatureFlag(flag.id, { name, description }, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['featureFlags'] });
      onCancel();
    },
    onError: (error) => onError(error.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      onError('Name is required');
      return;
    }

    if (isEditing) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  // Extract button content logic to avoid nested ternary
  const getButtonContent = () => {
    return isEditing ? 'Update' : 'Create';
  };

  return (
    <Box component='form' onSubmit={handleSubmit}>
      <Typography variant='h6' gutterBottom>
        {isEditing ? 'Edit Feature Flag' : 'Create Feature Flag'}
      </Typography>

      <TextField
        label='Name'
        fullWidth
        margin='normal'
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        disabled={isPending}
      />

      <TextField
        label='Description'
        fullWidth
        margin='normal'
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        multiline
        rows={2}
        disabled={isPending}
      />

      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
        <Button type='submit' variant='contained' loading={isPending}>
          {getButtonContent()}
        </Button>
        <Button onClick={onCancel} loading={isPending}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
}
